import { useState } from 'react';

import axios, { AxiosError } from 'axios';

import { CONTENT_TYPE } from 'common/constants';
import { HTTP_STATUS_CODES } from 'common/constants/HttpStatusConstants';
import { PROGRESS_BAR_STATES } from 'common/constants/ProgressConstants';
import {
	clearUploadedDocumentProgress,
	setUploadedDocumentProgress,
} from 'common/store/reducer/commonReducer';
import { getHeader } from 'common/utils/utilFunctions';

import { fileUploadCompleteHandler } from 'features/OpenRiskGPT/OpenRiskGPTUtil';

import { multiFileUploadHandler } from './commonHooksUtils';

let abortController = new AbortController();
const { COMPLETE } = PROGRESS_BAR_STATES;

export const useUploadForm = (
	url: string,
	updateState?: any,
	loggedInUser?: string,
) => {
	const { INTERNAL_SERVER_ERROR } = HTTP_STATUS_CODES;

	const [progress, setProgress] = useState(0);
	const [uploadResponse, setUploadResponse] = useState<any>();
	const [uploadError, setUploadError] = useState<any>();
	const [isSuccess, setIsSuccess] = useState<boolean>(false);
	const [multiFileUploadFailed, setMultiFileUploadFailed] = useState(false);
	const [reUploadFlag, setReUploadFlag] = useState(false);

	const handleMultipleApiResponses = (
		files: any[],
		response: { status: number }[],
		isReUpload?: boolean,
	) => {
		const allSuccess = response.filter(
			(val) => val.status === INTERNAL_SERVER_ERROR,
		);

		if (allSuccess.length === 0) {
			fileUploadCompleteHandler(files);
			setUploadResponse(response);
			setIsSuccess(true);
		} else {
			fileUploadCompleteHandler(files);
			setUploadResponse(response);
			setMultiFileUploadFailed(true);
			setIsSuccess(false);
		}

		if (isReUpload) {
			setReUploadFlag(!reUploadFlag);
		}
	};

	const uploadForm = async (
		formData: FormData,
		multiFile?: boolean,
		files?: any[],
		isReUpload?: boolean,
	) => {
		try {
			setIsSuccess(false);
			if (multiFile && files) {
				files.forEach(() => {
					if (updateState) {
						updateState(clearUploadedDocumentProgress());
					}
				});

				const response = await makeMultipleAPICalls(files);
				if (response) {
					handleMultipleApiResponses(files, response, isReUpload);
				}
			} else {
				onSingleFileUpload(formData);
			}
		} catch (e) {
			setUploadError(e);
			setIsSuccess(false);
			setProgress(0);
			if (axios.isCancel(e)) {
				abortController = new AbortController();
			} else {
				const error = e as AxiosError;
				setUploadError(error);
			}
		}
	};

	const makeMultipleAPICalls = async (files: any[]) => {
		const promises = Promise.all(
			files.map((fileInfo: any, index: number) => {
				const iformData = new FormData();
				iformData.append('file', fileInfo);
				return callUploadApi(iformData, index, true); // Added return statement
			}),
		);
		return promises;
	};
	const onSingleFileUpload = async (formData: FormData) => {
		try {
			setIsSuccess(false);
			const response = await axios.post(url, formData, {
				headers: {
					'X-Tenant-Id': 'pwc',
					'user-email-id': loggedInUser ?? '',
				},
				signal: abortController.signal,
				onUploadProgress: (progressEvent: any) => {
					const progress =
						(progressEvent.loaded / progressEvent.total) * COMPLETE;
					setProgress(progress);
				},
			});
			setUploadResponse(response);
			setIsSuccess(true);
		} catch (e) {
			setUploadError(e);
			setIsSuccess(false);
			setProgress(0);
			if (axios.isCancel(e)) {
				abortController = new AbortController();
			} else {
				const error = e as AxiosError;
				setUploadError(error?.response?.data);
			}
		}
	};
	const cancelUpload = () => {
		abortController.abort();
	};

	const callUploadApi = async (
		formData: FormData,
		index: number,
		multiFile?: boolean,
	) => {
		const result: any = await axios
			.post(url, formData, {
				headers: {
					'Content-Type': CONTENT_TYPE.MULTIPART,
					...getHeader(),
				},
				signal: abortController.signal,
				onUploadProgress: (progressEvent: any) => {
					if (!multiFile) {
						const progress =
							(progressEvent.loaded / progressEvent.total) * COMPLETE;
						setProgress(progress);
					} else if (progressEvent.total && updateState) {
						multiFileUploadHandler(progressEvent, index, updateState);
					}
				},
			})
			.then((response) => {
				if (updateState) {
					updateState(
						setUploadedDocumentProgress({ index, progress: COMPLETE }),
					);
				}
				const successObj = {
					status: response?.status,
					data: response?.data,
					fileInfo: formData.get('file'),
				};
				return successObj;
			})
			.catch((error) => {
				if (updateState) {
					updateState(
						setUploadedDocumentProgress({ index, progress: COMPLETE }),
					);
				}
				const responseObj = {
					status: INTERNAL_SERVER_ERROR,
					data: error,
					fileInfo: formData.get('file'),
				};
				return responseObj;
			});

		return result;
	};

	return {
		uploadForm,
		progress,
		cancelUpload,
		uploadResponse,
		uploadError,
		setUploadError,
		isSuccess,
		multiFileUploadFailed,
		reUploadFlag,
	};
};
